import { useMemo } from "react";
import { SimpleTable } from "@/core/components/simple-table";
import { Card } from "@volca/ui/components/ui/card";
import { Stack } from "@volca/ui/components/ui/stack";
import { Text } from "@volca/ui/components/ui/text";
import { useInView } from "@/core/hooks";
import { IcmAnimatedMoney } from "@/icm/common";
import {
  getTotalPayout,
  visibleTraceKeys,
} from "@/icm/icm-statement-page/utils";
import type { IcmLineItem } from "@/icm/types";
import { getTableMinWidth } from "@/icm/utils";
import {
  BusinessUnitCol,
  CustomerCol,
  JobCompletedCol,
  PayoutAmountCol,
  TraceCol,
} from "./cols";

interface IcmStatementRuleTableProps {
  ruleName: string;
  items: IcmLineItem[];
  selectedLineItemId: string | null;
  onSelectLineItem: (lineItem: IcmLineItem) => void;
}

export const IcmStatementRuleTable = ({
  ruleName,
  items,
  selectedLineItemId,
  onSelectLineItem,
}: IcmStatementRuleTableProps) => {
  const columns = useMemo(
    () => [
      CustomerCol(),
      BusinessUnitCol(),
      JobCompletedCol(),
      ...visibleTraceKeys(items).map(TraceCol),
      PayoutAmountCol(),
    ],
    [items],
  );

  const tableMinWidth = useMemo(() => getTableMinWidth(columns), [columns]);
  const totalPayout = useMemo(() => getTotalPayout(items), [items]);
  const [headerRef, isHeaderInView] = useInView({
    triggerOnce: true,
    threshold: 0.2,
  });

  return (
    <Stack className="w-full" align="start" gap={2}>
      <Stack
        gap={4}
        ref={headerRef}
        direction="row"
        align="baseline"
        className="gap-2.5"
      >
        <Text variant="title">{ruleName}</Text>
        <Stack
          align="start"
          gap={4}
          className="rounded-full bg-neutral/60 px-2.5 py-1"
        >
          <IcmAnimatedMoney
            value={isHeaderInView ? totalPayout : 0}
            className="items-baseline text-base leading-[1.1] font-medium tracking-[-0.02em] text-subtle"
            centsClassName="text-xs font-medium text-subtle"
          />
        </Stack>
      </Stack>
      <Card className="w-full gap-0 overflow-hidden py-0">
        <SimpleTable
          data={items}
          columns={columns}
          columnPinning={{
            left: ["customer_name"],
            right: ["payout_amount"],
          }}
          onRowClick={onSelectLineItem}
          selectedRowId={selectedLineItemId}
          getRowVariant={(item) =>
            item.warnings?.length ? "warning" : undefined
          }
          scrollAreaHeightClassName="h-auto"
          scrollAreaClassName="pr-0"
          scrollContentStyle={{ minWidth: tableMinWidth }}
        />
      </Card>
    </Stack>
  );
};
